"use client";
import { useState, useMemo } from "react";
import Link from "@/components/Link";
import {
  TextInput,
  Anchor as MantineAnchor,
  Box as MantineBox,
  Image as MantineImage,
  Text as MantineText,
} from "@mantine/core";
import { useFallbackProductImage } from "@/hooks/useFallbackProductImage";
import { generateBrandSlug } from "@/lib/shared/utils";

import styles from "./styles.module.scss";

const getBrandImageUrl = (brand?: BrandFilter) => {
  if (brand?.image?.includes("kurb.online")) {
    return brand.image?.replace("https://kurb.online", "").replace(".jpg", ".webp");
  }
  return null;
};

const BrandCard = ({ brand, dimmed }: { brand: BrandFilter; dimmed?: boolean }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const primaryImageUrl = getBrandImageUrl(brand);
  const fallbackImage = useFallbackProductImage({
    type: "brand",
    id: brand.id,
    label: brand.label,
    primaryImageUrl,
    primaryImageError: imageError,
    enabled: !primaryImageUrl || imageError,
  });

  const brandInitial = brand.label.charAt(0).toUpperCase() || "-";
  const displayImage = imageError || !primaryImageUrl ? fallbackImage : primaryImageUrl;
  const hasImage = !!displayImage;
  const showInitial = !hasImage || (!imageLoaded && primaryImageUrl && !imageError);
  const brandSlug = generateBrandSlug(brand.label);

  return (
    <Link
      key={brand.id || brand.label}
      href={`/brands/${brandSlug}`}
      prefetch={false}
      className={styles.brandCard}
      data-dimmed={dimmed}
    >
      <MantineBox className={styles.brandImage} style={{ position: "relative" }}>
        <MantineText
          span
          className={styles.brandInitial}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: showInitial ? 1 : 0,
            transition: "opacity 0.2s",
          }}
        >
          {brandInitial}
        </MantineText>
        {hasImage && (
          <MantineImage
            src={displayImage!}
            alt={brand.label}
            loading="lazy"
            style={{
              opacity: imageLoaded || (imageError && fallbackImage) ? 1 : 0,
              transition: "opacity 0.2s",
            }}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}
      </MantineBox>
      <MantineBox className={styles.brandName}>{brand.label}</MantineBox>
    </Link>
  );
};

const BrandsList = ({ brands }: { brands: BrandFilter[] }) => {
  const [searchQuery, setSearchQuery] = useState("");

  const { matched, unmatched } = useMemo(() => {
    const sorted = [...brands].sort((a, b) => a.label.localeCompare(b.label));
    if (!searchQuery) return { matched: sorted, unmatched: [] };
    const q = searchQuery.toLowerCase();
    return {
      matched: sorted.filter((b) => b.label.toLowerCase().includes(q)),
      unmatched: sorted.filter((b) => !b.label.toLowerCase().includes(q)),
    };
  }, [brands, searchQuery]);

  return (
    <>
      <MantineBox className={styles.introText}>
        <MantineText>
          If there are brands you would like us to add please reach out at{" "}
          <MantineAnchor href="mailto:contact@kurb.online">contact@kurb.online</MantineAnchor> or
          tell us on{" "}
          <MantineAnchor
            href="https://discord.gg/jSh7n6ju8H"
            target="_blank"
            rel="noopener noreferrer"
          >
            Discord.
          </MantineAnchor>
        </MantineText>
      </MantineBox>

      <MantineBox className={styles.filterControls}>
        <TextInput
          placeholder="Search for a brand"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          classNames={{
            root: styles.searchInput,
            input: styles.mantineInput,
          }}
        />
      </MantineBox>

      <MantineBox className={styles.brandsGrid}>
        {matched.map((brand: BrandFilter) => (
          <BrandCard key={brand.id || brand.label} brand={brand} />
        ))}
        {unmatched.map((brand: BrandFilter) => (
          <BrandCard key={brand.id || brand.label} brand={brand} dimmed />
        ))}
      </MantineBox>
    </>
  );
};

export default BrandsList;
